import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { styled } from "@mui/material/styles";
import DataStoreLoader from "csi/datastore/DataStoreLoader";
import DataStoreSelectionStore from "csi/datastore/DataStoreSelectionStore";
import { IDataStore } from "csi/typings";
import { useSyncLocalStore } from "designSystem/hooks";
import { white } from "designSystem/styles/color";
import { navListStyle } from "designSystem/styles/style-functions";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, ReactNode, useEffect, useState } from "react";
import Box from "../box/Box";
import IconButton from "../button/IconButton";
import CssGrid, { CssGridProps } from "../cssGrid/CssGrid";
import RequestErrorPlaceholder from "../placeholder/RequestErrorPlaceholder";
import xml from "csi/xml";

// TODO: Introduce way to indicate store will be changing soon, so render skeletons (such as when debouncing the changing of search text)

const listItemClass = "DataGridListItem";

const StyledIconButton = styled(IconButton)(({
	theme,
}) => ({
	backgroundColor: white[50],
	left: theme.spacing(3),
	position: "absolute",
	top: theme.spacing(3),
}));

const StyledBox = styled(Box)(({
	theme,
}) => ({
	height: "100%",
	position: "relative",
}));

const StyledCssGrid = styled(CssGrid)(({
	theme,
}) => ({
	[`& .${listItemClass}`]: {
		...navListStyle(theme),
		borderColor: "transparent",
		padding: theme.spacing(3),
	},
}));

export interface DataGridCardProps<T> {
	/** class to apply to the card */
	className?: string;

	/** id for item */
	id: string;

	/** data store item */
	item: T;

	/** handler for click */
	onClick: VoidFunction;

	/** handler for deleting nodes in store */
	onDelete?: VoidFunction;

	/** indicates if item is selected */
	selected?: boolean;

	/** indicates if grid allows selections */
	selecting?: boolean;

	/** reference to store containing item */
	store: IDataStore<T>;

	/** reference to additional props/ information for item */
	additionalProps?: Object;
}

export interface DataGridSelectionProps {
	/** indicates if selection is disabled */
	disabled: boolean;

	/** indicates if item is selected */
	selected: boolean;

	/** callback function when selection toggled */
	onToggle: VoidFunction;
}

export interface SelectCardComponentProps<T> extends DataGridCardProps<T> {
	/** component to use for each card/result in grid */
	CardComponent: FunctionComponent<DataGridCardProps<T>>;

	/** component used to handle selection of item */
	SelectionComponent?: FunctionComponent<DataGridSelectionProps>;

	/** if provided, then allows selection of store items and contains initial list of selections */
	selectionStore?: DataStoreSelectionStore;
}

const DefaultSelectionComponent: FunctionComponent<DataGridSelectionProps> = (props) => {
	const { disabled, selected, onToggle } = props;

	return (
		<StyledIconButton
			design="primary"
			disabled={disabled}
			onClick={onToggle}
			round
			shadow
			size="large"
		>
			{selected ?
				<CheckCircleIcon />
				:
				<RadioButtonUncheckedIcon />
			}
		</StyledIconButton>
	);
};

const SelectCardComponent = observer(<T extends unknown>(props: SelectCardComponentProps<T>) => {
	const { CardComponent, SelectionComponent, selected, selectionStore, ...otherProps } = props;
	const { addSelection, isSelectionDisabled, removeSelection } = selectionStore;
	const { id } = props;
	const disabled = isSelectionDisabled(id);

	const onToggle = () => {
		if (selected) {
			removeSelection(id);
		} else {
			addSelection(id);
		}
	};

	return (
		<StyledBox>
			<CardComponent
				selected={selected}
				{...otherProps}
			/>
			<SelectionComponent
				disabled={disabled}
				onToggle={onToggle}
				selected={selected}
			/>
		</StyledBox>
	);
});

export type designType = "default" | "list";

export interface DataGridProps<T> extends Partial<CssGridProps> {
	/** component to use for each card/result in grid */
	CardComponent: FunctionComponent<DataGridCardProps<T>>;

	/** component to be rendered first in grid to allow creating a new item */
	NewCardComponent?: FunctionComponent;

	/** component to use as placeholder when no results */
	PlaceholderComponent?: FunctionComponent;

	/** component used to handle selection of item*/
	SelectionComponent?: FunctionComponent<DataGridSelectionProps>;

	/** component to use to render skeletons while items are loaded */
	SkeletonComponent?: FunctionComponent;

	/** if configured, under the "list" mode, would make this list item active */
	activeItemId?: string;

	/** reference to additional props/ information for item */
	additionalProps?: Object;

	/** boolean representing if we want to always auto-select items in the store when none are selected, and when deleting items */
	autoSelect?: boolean;

	/** design of the item, if configured as list, it would apply extra styling to it during hovering, selected, etc. */
	design?: designType;

	/** handler for click */
	onClickItem?: (item: T) => void;

	/** handler for deleting nodes in store */
	onDeleteItem?: (id: string) => void;

	/** incremental number of rows to load and render at a time */
	rowsPerPage?: number;

	/** if provided, then allows selection of store items and contains initial list of selections */
	selectionStore?: DataStoreSelectionStore;

	/** boolean representing if we want to show a number in the list, pass to card to display */
	showIndex?: boolean;

	/** data store */
	store: IDataStore<T>;
}

const DataGrid = observer(<T extends unknown>(props: DataGridProps<T>) => {
	const loader = useSyncLocalStore(() => new DataStoreLoader<T>());

	const {
		CardComponent,
		NewCardComponent,
		PlaceholderComponent,
		SelectionComponent,
		SkeletonComponent,
		activeItemId,
		additionalProps,
		autoSelect,
		design,
		onClickItem,
		onDeleteItem,
		rowsPerPage,
		selectionStore,
		showIndex,
		store,
		...otherProps
	} = props;

	const {
		error,
		hasMoreResults,
		hasNoResultsAfterLoading,
		hasResults,
		loadMoreResults,
		numberOfSkeletons,
		resultsWithIds,
		retry,
		setColumns,
	} = loader;

	const [selectedId, setSelectedId] = useState<string>("");
	const isListItem = design === "list";

	const ItemComponent = selectionStore ? SelectCardComponent : CardComponent;
	const itemProps = selectionStore ? {
		CardComponent,
		selecting: true,
		SelectionComponent: SelectionComponent || DefaultSelectionComponent,
		selectionStore,
	} : null;

	// update rows per page - not observable so just assign and will be used on next query
	if (rowsPerPage) {
		loader.rowsPerPage = rowsPerPage;
	}

	// tell loader that it needs to load one less result to complete all rows since there is a new card being shown
	if (NewCardComponent) {
		loader.firstPageOffset = 1;
	}

	// ensure that loader is updated with the latest store
	useEffect(() => {
		const { columns, columnsByWidth } = props;

		loader.setStore(store);

		if (columns && !columnsByWidth) {
			loader.setColumns(columns);
		}
	}, [store]);

	/*
	* if auto-select is enabled, there are at least one item in store, and there are no items selected, auto-select the first item
	* otherwise, empty out selected item
	*/
	useEffect(() => {
		if (autoSelect) {
			const { total } = store;
			if (total > 0 && !xml.isValidURL(selectedId)) {
				const selectedItem = store.get(0);
				const selectedId = store.getIdentity(selectedItem);
				_onClick(selectedId, selectedItem);
			} else if (total === 0) {
				_onClick(null, null);
			}
		}
	}, [store?.total]);

	if (error) {
		return (
			<RequestErrorPlaceholder onRetry={retry} secondary />
		);
	}

	const children: ReactNode[] = [];

	// show new card component if there are already results, or if grid is empty and done loading
	if (NewCardComponent && (!hasResults || resultsWithIds.length)) {
		children.push(<NewCardComponent key="__DataGridNew__" />);
	}

	// callback for on click
	const _onClick = (id: string, item: T) => {
		if (isListItem) {
			setSelectedId(id);
		}

		onClickItem?.(item);
	};

	// callback for on delete
	const _onDelete = (id: string, item: T) => {
		const index = store.getIdentityIndex(id);
		onDeleteItem?.(id);

		// Select item after deleted item if it exists, otherwise the item before. If the store is empty, set to selection null
		if (autoSelect) {
			const { total } = store;
			if (total > 0) {
				const selectedItem = index < total ? store.get(index) : store.get(total - 1);
				const selectedId = store.getIdentity(selectedItem);

				_onClick(selectedId, selectedItem);
			} else {
				_onClick(null, null);
			}
		}
	};

	if (hasNoResultsAfterLoading && !children.length) {
		return PlaceholderComponent ? <PlaceholderComponent /> : null;
	}

	if (hasResults || children.length) {
		let index = 0;

		for (const [item, id] of resultsWithIds) {
			const selected = selectionStore?.isSelected(id);
			let className;
			if (showIndex) {
				index += 1;
				item["_indexForList"] = index;
			}

			if (isListItem) {
				const active = id === (activeItemId || selectedId || resultsWithIds[0][1]);
				className = `${listItemClass} ${active ? "Mui-selected" : ""}`;
			}

			children.push(<ItemComponent
				additionalProps={additionalProps}
				className={className}
				id={id}
				item={item}
				key={id}
				onClick={() => _onClick(id, item)}
				onDelete={() => _onDelete(id, item)}
				selected={selected}
				store={store}
				{...itemProps}
			/>);
		}

		if (SkeletonComponent) {
			for (let i = 0; i < numberOfSkeletons; ++i) {
				children.push(<SkeletonComponent key={`__Skeleton${i}`} />);
			}
		}

		return (
			<StyledCssGrid
				hasMoreResults={hasMoreResults}
				infiniteScrolling
				onMoreResults={loadMoreResults}
				onNumberOfColumns={setColumns}
				{...otherProps}
			>
				{children}
			</StyledCssGrid>
		);
	}

	return PlaceholderComponent ? <PlaceholderComponent /> : null;
});

export default DataGrid;